import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export function CompanyMediaPreview({ companyId, kind, revision = "", alt, className, fallback = null }: {
  companyId: string; kind: "logo" | "cover"; revision?: string | number;
  alt: string; className: string; fallback?: ReactNode;
}) {
  const { user } = useAuth();
  const scope = `${user?.id ?? "guest"}:${user?.activeContext ?? "personal"}:${companyId}:${kind}:${revision}`;
  const [preview, setPreview] = useState<{ scope: string; url: string | null; failed: boolean } | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const abort = new AbortController(); let url: string | null = null;
    setPreview(null);
    // [business.media](../../../docs/product/businesses.md#business-media): native image tags cannot carry the session's Bearer token.
    // Fetch private bytes through the API; cancel late reads and release them
    // when the account, profile or saved media changes.
    void api.companyMediaBlob(companyId, kind, abort.signal).then(blob => {
      if (abort.signal.aborted) return;
      url = URL.createObjectURL(blob); setPreview({ scope, url, failed: false });
    }).catch((failure: unknown) => {
      if (!abort.signal.aborted) setPreview({ scope, url: null, failed: !(failure instanceof ApiError && failure.status === 404) });
    });
    return () => { abort.abort(); if (url) URL.revokeObjectURL(url); };
  }, [companyId, kind, scope, retry]);
  const current = preview?.scope === scope ? preview : null;
  if (current?.url) return <img src={current.url} alt={alt} className={className} />;
  if (current?.failed && fallback === null) return <div className="text-xs text-mut"><button type="button" onClick={() => setRetry(value => value + 1)} className="underline">Retry saved {kind}</button></div>;
  return <>{fallback}</>;
}
